<template>
  <header
    class="hosting-header"
    :style="{
      background: theme.background,
      color: theme.text
    }"
  >
    <!-- ====================================================== -->
    <!-- TOP UTILITY BAR -->
    <!-- ====================================================== -->

    <div class="hosting-utility-bar">
      <div class="utility-left">
        <button
          type="button"
          class="utility-contact"
        >
          {{
            currentLanguage === 'fr'
              ? 'Contactez-nous'
              : 'Contact us'
          }}
        </button>

        <!-- ACCOUNT -->

        <v-menu>
          <template #activator="{ props }">
            <button
              v-bind="props"
              type="button"
              class="account-button"
            >
              <v-icon size="14">
                mdi-account
              </v-icon>

              <span class="account-name">
                {{ displayName }}
              </span>

              <v-icon size="15">
                mdi-chevron-down
              </v-icon>
            </button>
          </template>

          <v-list density="compact">
            <v-list-item
              prepend-icon="mdi-account-outline"
              :title="
                currentLanguage === 'fr'
                  ? 'Mon profil'
                  : 'My Profile'
              "
            />

            <v-list-item
              prepend-icon="mdi-history"
              :title="
                currentLanguage === 'fr'
                  ? 'Activité'
                  : 'Activity'
              "
              @click="goTo('SaaSHostingActivity')"
            />

            <v-divider />

            <v-list-item
              prepend-icon="mdi-logout-variant"
              :title="
                currentLanguage === 'fr'
                  ? 'Déconnexion'
                  : 'Logout'
              "
              @click="handleLogout"
            />
          </v-list>
        </v-menu>
      </div>

      <!-- ====================================================== -->
      <!-- RIGHT SIDE -->
      <!-- ====================================================== -->

      <div class="utility-right">
        <v-badge
          :content="hostingCart.itemCount"
          :model-value="hostingCart.itemCount > 0"
          color="error"
          location="top end"
        >
          <v-btn
            icon
            size="x-small"
            variant="text"
            :aria-label="
              currentLanguage === 'fr'
                ? 'Panier'
                : 'Cart'
            "
            @click="goToHostingCheckout"
          >
            <v-icon size="16">
              mdi-cart-outline
            </v-icon>
          </v-btn>
        </v-badge>

        <button
          type="button"
          class="currency-button"
        >
          C$ CAD
        </button>

        <v-btn
          icon
          size="x-small"
          variant="text"
          aria-label="Search"
        >
          <v-icon size="16">
            mdi-magnify
          </v-icon>
        </v-btn>
      </div>
    </div>

    <!-- ====================================================== -->
    <!-- MAIN HEADER -->
    <!-- ====================================================== -->

    <div class="hosting-main-header">
      <!-- ==================================================== -->
      <!-- SIDEBAR TOGGLE -->
      <!-- ==================================================== -->

      <div class="sidebar-controls">
        <v-btn
          icon
          size="small"
          variant="text"
          @click="$emit('toggle-sidebar')"
        >
          <v-icon>
            mdi-menu
          </v-icon>
        </v-btn>

        <v-btn
          v-if="mdAndUp"
          icon
          size="small"
          variant="text"
          @click="$emit('toggle-pin')"
        >
          <v-icon
            :color="
              pinned
                ? 'primary'
                : undefined
            "
          >
            mdi-pin
          </v-icon>
        </v-btn>
      </div>

      <!-- ==================================================== -->
      <!-- LOGO -->
      <!-- ==================================================== -->

      <router-link
        :to="homeRoute"
        class="hosting-brand"
      >
        <v-img
          v-if="currentLogo"
          :src="currentLogo"
          class="hosting-logo"
          contain
        />

        <div
          v-else
          class="fallback-brand"
        >
          <div class="fallback-mark">
            N
          </div>

          <span>
            nexgbits
          </span>
        </div>
      </router-link>

      <!-- ==================================================== -->
      <!-- DESKTOP NAVIGATION -->
      <!-- ==================================================== -->

      <nav
        v-if="mdAndUp"
        class="hosting-nav"
      >
        <button
          v-for="item in navigation"
          :key="item.key"
          type="button"
          class="hosting-nav-item"
          :class="{
            active: isNavigationActive(item)
          }"
          @click="navigate(item)"
        >
          <span
            v-if="item.badge"
            class="nav-badge"
            :class="{
              blue: item.badgeType === 'blue'
            }"
          >
            {{ item.badge }}
          </span>

          <span>
            {{ item.title }}
          </span>
        </button>
      </nav>

      <v-spacer />

      <!-- ==================================================== -->
      <!-- RIGHT HEADER ACTIONS -->
      <!-- ==================================================== -->

      <div class="header-actions">
        <!-- THEME -->

        <v-menu
          v-model="themeMenu"
          :close-on-content-click="false"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              size="small"
              variant="text"
            >
              <v-icon size="19">
                mdi-theme-light-dark
              </v-icon>
            </v-btn>
          </template>

          <v-card min-width="280">
            <v-card-title class="text-subtitle-1">
              {{
                currentLanguage === 'fr'
                  ? 'Apparence'
                  : 'Appearance'
              }}
            </v-card-title>

            <v-divider />

            <v-list density="compact">
              <v-list-item
                prepend-icon="mdi-white-balance-sunny"
                title="Light"
                @click="selectTheme('light')"
              >
                <template #append>
                  <v-icon
                    v-if="currentTheme === 'light'"
                    color="primary"
                  >
                    mdi-check
                  </v-icon>
                </template>
              </v-list-item>

              <v-list-item
                prepend-icon="mdi-weather-night"
                title="Dark"
                @click="selectTheme('dark')"
              >
                <template #append>
                  <v-icon
                    v-if="currentTheme === 'dark'"
                    color="primary"
                  >
                    mdi-check
                  </v-icon>
                </template>
              </v-list-item>

              <v-list-item
                prepend-icon="mdi-theme-light-dark"
                title="Hybrid"
                @click="selectTheme('hybrid')"
              >
                <template #append>
                  <v-icon
                    v-if="currentTheme === 'hybrid'"
                    color="primary"
                  >
                    mdi-check
                  </v-icon>
                </template>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <!-- LANGUAGE -->

        <LangSwitch />

        <!-- MESSAGES -->

        <v-btn
          icon
          size="small"
          variant="text"
        >
          <v-badge
            v-if="messageCount > 0"
            :content="messageCount"
            color="error"
          >
            <v-icon size="18">
              mdi-email-outline
            </v-icon>
          </v-badge>

          <v-icon
            v-else
            size="18"
          >
            mdi-email-outline
          </v-icon>
        </v-btn>
      </div>
    </div>
  </header>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref
} from 'vue'

import {
  useRoute,
  useRouter
} from 'vue-router'

import {
  useDisplay
} from 'vuetify'

import LangSwitch
  from '@/components/LangSwitch.vue'

import {
  useAuthStore,
  useThemeStore
} from '@/store/auth'

import {
  useLangStore
} from '@/store/lang'

import {
  useHostingCartStore
} from '@/store/hostingCart'

// ============================================================
// PROPS / EVENTS
// ============================================================

defineProps({
  pinned: {
    type: Boolean,
    default: false
  }
})

defineEmits([
  'toggle-sidebar',
  'toggle-pin'
])

// ============================================================
// STORES
// ============================================================

const auth =
  useAuthStore()

const themeStore =
  useThemeStore()

const langStore =
  useLangStore()

const hostingCart =
  useHostingCartStore()

const router =
  useRouter()

const route =
  useRoute()

const {
  mdAndUp
} =
  useDisplay()

// ============================================================
// STATE
// ============================================================

const themeMenu =
  ref(false)

const messageCount =
  ref(0)

// ============================================================
// LANGUAGE
// ============================================================

const currentLanguage =
  computed(() =>
    langStore.currentLanguage ||
    'en'
  )

// ============================================================
// THEME
// ============================================================

const theme =
  computed(() =>
    themeStore.themeStyles
  )

const currentTheme =
  computed(() =>
    localStorage.getItem(
      'currentTheme'
    ) ||
    'light'
  )

function selectTheme(
  selectedTheme
) {
  themeStore.setTheme(
    selectedTheme
  )

  localStorage.setItem(
    'currentTheme',
    selectedTheme
  )

  themeMenu.value =
    false
}

// ============================================================
// LOGO
// ============================================================

const currentLogo =
  computed(() => {
    const selectedTheme =
      localStorage.getItem(
        'currentTheme'
      ) ||
      'light'

    if (
      selectedTheme ===
      'dark'
    ) {
      return (
        localStorage.getItem(
          'LogoDark'
        ) ||
        ''
      )
    }

    return (
      localStorage.getItem(
        'LogoLight'
      ) ||
      ''
    )
  })

// ============================================================
// USER
// ============================================================

const displayName =
  computed(() =>
    auth.loginName ||
    auth.user?.name ||
    'Customer'
  )

// ============================================================
// HOME
// ============================================================

const homeRoute =
  computed(() => ({
    name:
      'PublicHome',

    params: {
      lang:
        currentLanguage.value
    }
  }))

// ============================================================
// NAVIGATION
// ============================================================

const navigation =
  computed(() => [
    {
      key:
        'domains',

      title:
        currentLanguage.value === 'fr'
          ? 'Domaines'
          : 'Domains',

      routeName:
        'SaaSHostingSubdomains',

      badge:
        'NEW'
    },

    {
      key:
        'hosting',

      title:
        currentLanguage.value === 'fr'
          ? 'Hébergement'
          : 'Hosting',

      routeName:
        'SaaSHostingAccount'
    },

    {
      key:
        'email',

      title:
        currentLanguage.value === 'fr'
          ? 'Courriel'
          : 'Email'
    },

    {
      key:
        'marketing',

      title:
        currentLanguage.value === 'fr'
          ? 'Outils marketing'
          : 'Marketing Tools',

      badge:
        'NEW'
    },

    {
      key:
        'security',

      title:
        currentLanguage.value === 'fr'
          ? 'Sécurité'
          : 'Security',

      badge:
        'NEW'
    },

    {
      key:
        'transfer',

      title:
        currentLanguage.value === 'fr'
          ? 'Transférer chez nous'
          : 'Transfer to Us',

      badge:
        currentLanguage.value === 'fr'
          ? 'ESSAI'
          : 'TRY ME',

      badgeType:
        'blue'
    },

    {
      key:
        'help',

      title:
        currentLanguage.value === 'fr'
          ? "Centre d'aide"
          : 'Help Center',

      badge:
        'NEW'
    },

    {
      key:
        'account',

      title:
        currentLanguage.value === 'fr'
          ? 'Compte'
          : 'Account',

      routeName:
        'SaaSHostingDashboard',

      badge:
        'NEW'
    }
  ])

// ============================================================
// NAVIGATION METHODS
// ============================================================

function navigate(
  item
) {
  if (!item.routeName) {
    return
  }

  router.push({
    name:
      item.routeName,

    params: {
      lang:
        currentLanguage.value
    }
  })
}

function goTo(
  routeName
) {
  router.push({
    name:
      routeName,

    params: {
      lang:
        currentLanguage.value
    }
  })
}

function isNavigationActive(
  item
) {
  return (
    item.routeName &&
    route.name ===
      item.routeName
  )
}

// ============================================================
// CART
// ============================================================

async function goToHostingCheckout() {
  if (
    hostingCart.isEmpty
  ) {
    return
  }

  hostingCart.persist()

  await router.push({
    name:
      currentLanguage.value === 'fr'
        ? 'CheckOutFR'
        : 'CheckOutEN'
  })
}

// ============================================================
// LOGOUT
// ============================================================

async function handleLogout() {
  await auth.logout()

  await router.push({
    name:
      'PublicHome',

    params: {
      lang:
        currentLanguage.value
    }
  })
}

// ============================================================
// MOUNT
// ============================================================

onMounted(() => {
  hostingCart.restore()
})
</script>

<style scoped>
/* ============================================================
   HEADER ROOT
============================================================ */

.hosting-header {
  width: 100%;
  position: relative;
  z-index: 1000;

  border-bottom:
    1px solid
    rgba(0, 0, 0, 0.08);
}

/* ============================================================
   UTILITY BAR
============================================================ */

.hosting-utility-bar {
  width: 100%;
  height: 28px;

  padding:
    0
    32px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background: #f0f1ff;
  color: #333333;

  font-size: 11px;
}

.utility-left,
.utility-right {
  display: flex;
  align-items: center;
}

.utility-contact,
.currency-button,
.account-button {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 11px;
}

.account-button {
  min-height: 22px;

  margin-left: 10px;

  padding:
    0
    7px;

  display: flex;
  align-items: center;
  gap: 4px;

  border-radius: 3px;

  background: #e2e4f1;
}

.account-name {
  max-width: 150px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* ============================================================
   MAIN HEADER
============================================================ */

.hosting-main-header {
  width: 100%;
  height: 56px;

  padding:
    0
    32px;

  display: flex;
  align-items: center;

  background: inherit;
}

/* ============================================================
   SIDEBAR CONTROLS
============================================================ */

.sidebar-controls {
  display: flex;
  align-items: center;
  margin-right: 5px;
}

/* ============================================================
   BRAND
============================================================ */

.hosting-brand {
  min-width: 205px;

  display: flex;
  align-items: center;

  text-decoration: none;
}

.hosting-logo {
  width: 180px;
  height: 42px;
}

.fallback-brand {
  display: flex;
  align-items: center;

  color: #6d6d6d;

  font-size: 20px;
}

.fallback-mark {
  width: 39px;
  height: 30px;

  margin-right: 7px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 4px;

  background: #ff6c27;

  color: #ffffff;

  font-size: 22px;
  font-weight: 900;
  font-style: italic;
}

/* ============================================================
   NAVIGATION
============================================================ */

.hosting-nav {
  height: 100%;

  display: flex;
  align-items: stretch;
}

.hosting-nav-item {
  position: relative;

  min-width: 64px;

  padding:
    0
    9px;

  border: 0;
  background: transparent;

  color: inherit;

  font-size: 12px;
  font-weight: 600;

  white-space: nowrap;

  cursor: pointer;
}

.hosting-nav-item:hover {
  background:
    rgba(
      128,
      128,
      128,
      0.08
    );
}

.hosting-nav-item.active {
  font-weight: 700;
}

.nav-badge {
  position: absolute;

  top: 3px;
  left: 8px;

  padding:
    0
    3px;

  border-radius: 2px;

  background: #f36b21;
  color: white;

  font-size: 7px;
  font-weight: 800;
  line-height: 12px;
}

.nav-badge.blue {
  background: #1976d2;
}

/* ============================================================
   ACTIONS
============================================================ */

.header-actions {
  display: flex;
  align-items: center;
  gap: 3px;
}

/* ============================================================
   TABLET
============================================================ */

@media (max-width: 1200px) {
  .hosting-main-header {
    padding:
      0
      16px;
  }

  .hosting-utility-bar {
    padding:
      0
      16px;
  }

  .hosting-brand {
    min-width: 170px;
  }

  .hosting-logo {
    width: 150px;
  }

  .hosting-nav-item {
    padding:
      0
      6px;

    font-size: 11px;
  }
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 959px) {
  .hosting-utility-bar {
    padding:
      0
      10px;
  }

  .hosting-main-header {
    padding:
      0
      8px;
  }

  .hosting-brand {
    min-width: unset;
  }

  .hosting-logo {
    width: 145px;
    height: 38px;
  }

  .sidebar-controls {
    margin-right: 0;
  }
}

@media (max-width: 600px) {
  .utility-contact {
    display: none;
  }

  .account-button {
    margin-left: 0;
  }

  .currency-button {
    display: none;
  }

  .hosting-logo {
    width: 125px;
  }
}
</style>